// chat/actions.ts — the executable side of the in-app App Brain chat.
// Every action maps to THIS app's own on-device store (store.ts) or its existing
// AI endpoint (brain.ts::closetRecap). Nothing new server-side; no new endpoints.
import { store, todayLocal, type Garment, type GarmentPatch } from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "closet";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave, suggestOutfits } from "../brain";
import { addInstruction } from "./brain";
import { closetRecap, BrainError } from "../brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

export interface ClosetFilter {
  text?: string;
  category?: string;
  color?: string;
  neverWorn?: boolean;
  worn?: boolean;
}

/** A compact row shape for rendering filter/search results inside a chat bubble. */
export interface FilterRow {
  id: string;
  item: string;
  brand: string;
  category: string;
  color: string;
  wears: number;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_garment"; item: string; brand?: string | null; category?: string | null; color?: string | null; price_cents?: number; purchased_on?: string | null }
  | { kind: "log_wear"; garmentId: string; label?: string }
  | { kind: "edit_garment"; id: string; patch: GarmentPatch; label?: string }
  | { kind: "delete_garment"; id: string; label?: string }
  | { kind: "filter"; filter: ClosetFilter; label?: string }
  | { kind: "summarize" }
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "compose_outfit"; occasion?: string }
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string;
  changed?: boolean;
  rows?: FilterRow[];
}

/** Pure, local filter over the wardrobe — no network, no server. */
export function applyClosetFilter(items: Garment[], f: ClosetFilter): Garment[] {
  return items.filter((g) => {
    if (f.neverWorn && g.wear_count > 0) return false;
    if (f.worn && g.wear_count <= 0) return false;
    if (f.category && g.category.toLowerCase() !== f.category) return false;
    if (f.color && g.color.toLowerCase() !== f.color) return false;
    if (f.text) {
      const hay = `${g.item} ${g.brand} ${g.category} ${g.color}`.toLowerCase();
      if (!hay.includes(f.text)) return false;
    }
    return true;
  });
}

export async function executeAction(a: ChatAction, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_garment": {
        const g = await store.createGarment({
          item: a.item,
          brand: a.brand ?? undefined,
          category: a.category ?? undefined,
          color: a.color ?? undefined,
          price_cents: a.price_cents ?? undefined,
          purchased_on: a.purchased_on ?? undefined,
        });
        return { ok: true, message: `Added ${g.item}.`, changed: true };
      }

      case "log_wear":
        await store.logWear(a.garmentId, todayLocal());
        return { ok: true, message: `Logged a wear for ${a.label ?? "it"}.`, changed: true };

      case "edit_garment":
        await store.updateGarment(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the item"}.`, changed: true };

      case "delete_garment":
        await store.deleteGarment(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the item"}.`, changed: true };

      case "filter": {
        const all = await store.listGarments(50000);
        const rows = applyClosetFilter(all, a.filter);
        return {
          ok: true,
          message: `${rows.length} ${rows.length === 1 ? "match" : "matches"}`,
          rows: rows.slice(0, 50).map((g) => ({
            id: g.id,
            item: g.item,
            brand: g.brand,
            category: g.category,
            color: g.color,
            wears: g.wear_count,
          })),
        };
      }

      case "summarize": {
        // Reuse THIS app's existing AI endpoint: compute signals on-device, then narrate.
        const signals = await store.computeSignals(todayLocal());
        const { recap } = await closetRecap(signals);
        return { ok: true, message: recap || "Not enough data yet." };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // Photo-heavy backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup is photo-heavy, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path (garment photos add up). One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size, garment photos included.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large photo-heavy backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my wardrobe" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large (it can be, with garment photos), use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "compose_outfit": {
        // Compose outfits STRICTLY from garments the user owns (the app's fenced outfit endpoint).
        const all = await store.listGarments(50000);
        const { outfits, note } = await suggestOutfits(all, a.occasion ?? "");
        if (outfits.length === 0) return { ok: true, message: note || "Add a few more pieces first — outfits need clothes to combine." };
        const byId = new Map(all.map((g) => [g.id, g]));
        const text = outfits
          .map((o) => {
            const items = o.garment_ids.map((id) => byId.get(id)?.item).filter(Boolean);
            return `${o.name}\n${items.map((i) => `• ${i}`).join("\n")}${o.why ? `\n${o.why}` : ""}`;
          })
          .join("\n\n");
        return { ok: true, message: text + (note ? `\n\n${note}` : "") };
      }

      case "save_to_cloud": {
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
